import { lazy, Suspense, type FC, type ReactElement } from 'react';
import { useIntl, defineMessages, FormattedMessage, FormattedNumber } from 'dibs-react-intl';

import LikeHeartIcon from 'dibs-icons/exports/legacy/LikeHeartIcon.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';

// Styles
import classnames from 'classnames';
import styles from './Heart-Styles.scss';
import dibsCss from 'dibs-css';

const HeartPulseLazy = lazy(() => import(/* webpackChunkName: "HeartPulse" */ './HeartPulse.tsx'));

const messages = defineMessages({
    label: {
        id: 'dibs-portfolio-heart.HeartIconWrapper.label',
        defaultMessage: 'Save to favorites',
    },
});

const HeartCount: FC<{ count: number; isCircleLight: boolean }> = ({ count, isCircleLight }) => {
    let value = <FormattedNumber value={count} />;
    if (count > 999) {
        // If count is greater than 1000, display as value in thousands up to 999.9K
        const countInThousands = Math.min(999.9, count / 1000);
        // Round to tenth decimal
        const countToTenthDecimal = Math.round(countInThousands * 10) / 10;
        // Format number value
        const formattedCount = <FormattedNumber value={countToTenthDecimal} />;
        // Format value in thousands
        value = (
            <FormattedMessage
                id="HeartIcon.HeartCount.value"
                defaultMessage="{count}K"
                values={{ count: formattedCount }}
            />
        );
    }

    return (
        <div className={classnames(styles.heartCount, { [styles.marginTop]: isCircleLight })}>
            {value}
        </div>
    );
};

type WrapperProps = {
    children: ReactElement;
    onClick: () => void;
    wrapperComponent: 'Link' | 'div';
    buttonClass: string;
    theme:
        | 'dark'
        | 'light'
        | 'circle-light'
        | 'circle-frosted-small'
        | 'circle-frosted-medium'
        | 'circle-frosted-large';
};

const WrapperElement: FC<WrapperProps> = ({
    buttonClass,
    children,
    onClick,
    wrapperComponent,
    theme,
}) => {
    const isCircleLight = theme === 'circle-light';
    const isCircleFrostedSmall = theme === 'circle-frosted-small';
    const isCircleFrostedMedium = theme === 'circle-frosted-medium';
    const isCircleFrostedLarge = theme === 'circle-frosted-large';
    const isCircleFrosted = isCircleFrostedSmall || isCircleFrostedMedium || isCircleFrostedLarge;
    const intl = useIntl();
    return wrapperComponent === 'Link' ? (
        <Link
            className={classnames(
                styles.iconWrapper,
                { [styles.circledWrapper]: isCircleLight },
                {
                    [styles.circledFrostedWrapper]: isCircleFrosted,
                    [styles.circleFrostedWrapperSmall]: isCircleFrostedSmall,
                    [styles.circleFrostedWrapperMedium]: isCircleFrostedMedium,
                    [styles.circleFrostedWrapperLarge]: isCircleFrostedLarge,
                },
                buttonClass
            )}
            ariaLabel={intl.formatMessage(messages.label)}
            onClick={onClick}
        >
            {children}
        </Link>
    ) : (
        <div className={classnames(styles.iconWrapper, buttonClass)} onClick={onClick}>
            {children}
        </div>
    );
};

type Props = {
    className: string;
    buttonClass: string;
    isStrokeWidthHeavy: boolean;
    isAnimated: boolean;
    isFavorited: boolean;
    isFilled: boolean;
    useHoverClass: boolean;
    onClick: () => void;
    onAnimationEnd: () => void;
    wrapperComponent: 'div' | 'Link';
    hideTotalLikesCount: boolean;
    itemId: string;
    theme:
        | 'dark'
        | 'light'
        | 'circle-light'
        | 'circle-frosted-small'
        | 'circle-frosted-medium'
        | 'circle-frosted-large';
    heartCount: number;
    animateHeartPulse: boolean;
    showCircleGlow?: boolean;
};

const HeartIconWrapper: FC<Props> = ({
    className: classNameDef,
    buttonClass,
    isStrokeWidthHeavy = false,
    isAnimated,
    isFavorited = false,
    isFilled,
    useHoverClass = false,
    onClick,
    onAnimationEnd,
    wrapperComponent = 'Link',
    hideTotalLikesCount = false,
    itemId,
    theme,
    heartCount,
    animateHeartPulse,
    showCircleGlow,
}) => {
    const isCircleLight = theme === 'circle-light';
    const isCircleFrostedSmall = theme === 'circle-frosted-small';
    const isCircleFrostedMedium = theme === 'circle-frosted-medium';
    const isCircleFrostedLarge = theme === 'circle-frosted-large';
    const isCircleFrosted = isCircleFrostedSmall || isCircleFrostedMedium || isCircleFrostedLarge;
    const isCircle = isCircleLight || isCircleFrosted;
    const className = classnames(
        styles.heart,
        {
            [styles.circledHeart]: isCircleLight,
            [styles.circledFrostedHeart]: isCircleFrosted,
        },
        classNameDef
    );

    const iconClasses = classnames(styles.icon, {
        [styles.isStrokeWidthHeavy]: isStrokeWidthHeavy,
        [styles.hoverClass]: useHoverClass,
        [styles.dark]: theme === 'dark',
        [styles.light]: theme === 'light',
        [styles.circledIcon]: isCircleLight,
        [styles.circleFrosted]: isCircleFrosted,
    });

    const dataTn = isFavorited ? 'item-saved-icon' : 'item-save-icon';
    const id = `${itemId}-heart`;

    return (
        <>
            <WrapperElement
                onClick={onClick}
                wrapperComponent={wrapperComponent}
                buttonClass={classnames(buttonClass, { [dibsCss.overflowVisible]: showCircleGlow })}
                theme={theme}
            >
                <>
                    {showCircleGlow && <span className={styles.glow} />}
                    {/* Carefully with data-tn. it must always stay together with id. It's required for heart status detection. */}
                    <div
                        className={className}
                        onAnimationEnd={onAnimationEnd}
                        data-tn={dataTn}
                        id={id}
                    >
                        {animateHeartPulse && (
                            <Suspense fallback="">
                                <HeartPulseLazy isFilled={isFilled} isCircleLight={isCircleLight} />
                            </Suspense>
                        )}
                        <LikeHeartIcon
                            className={iconClasses}
                            animateHeartBeat={isAnimated}
                            isFilled={isFilled}
                            theme={theme}
                        />
                    </div>
                    {isCircle && heartCount > 0 && !hideTotalLikesCount && (
                        <HeartCount count={heartCount} isCircleLight={isCircleLight} />
                    )}
                </>
            </WrapperElement>
            {!isCircle && heartCount > 0 && !hideTotalLikesCount && (
                <HeartCount count={heartCount} isCircleLight={isCircleLight} />
            )}
        </>
    );
};

export default HeartIconWrapper;
